import React, { useState } from "react";
import StagingTestResult from "./StagingTestResult";

/**
 * Manual staging test control — never runs automatically.
 */
export default function StagingTestControl({
  title,
  method,
  pathDescription,
  pathBuilder,
  runTest,
  requiresMutationConfirm = false,
  requiresDestructiveConfirm = false,
  requiresUnsafeConfirm = false,
  disabled = false,
  externalInputs = {},
  children,
}) {
  const [mutationConfirmed, setMutationConfirmed] = useState(false);
  const [destructiveConfirmed, setDestructiveConfirmed] = useState(false);
  const [unsafeConfirmed, setUnsafeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [inputs, setInputs] = useState({});
  const mergedInputs = { ...externalInputs, ...inputs };

  const canRun =
    !disabled &&
    !loading &&
    (!requiresMutationConfirm || mutationConfirmed) &&
    (!requiresDestructiveConfirm || destructiveConfirmed) &&
    (!requiresUnsafeConfirm || unsafeConfirmed);

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      const path = pathBuilder ? pathBuilder(mergedInputs) : pathDescription;
      const res = await runTest(mergedInputs, path);
      setResult(res);
    } catch (err) {
      setResult({
        ok: false,
        status: err.status || 0,
        method,
        path: pathDescription,
        fullUrl: pathDescription,
        data: err.data || null,
        errorMessage: err.message || String(err),
        credentialsIncluded: true,
        csrfCookiePresent: false,
        csrfHeaderSent: false,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cs-staging-test">
      <h4 className="cs-staging-test__title">{title}</h4>
      <p className="cs-staging-test__endpoint">
        <code>{method}</code> {pathDescription}
      </p>

      {children ? (
        <div className="cs-staging-test__inputs">
          {React.Children.map(children, (child) =>
            React.isValidElement(child)
              ? React.cloneElement(child, {
                  inputs,
                  setInputs,
                })
              : child
          )}
        </div>
      ) : null}

      <div className="cs-staging-test__checks">
        {requiresMutationConfirm ? (
          <label className="cs-staging-check">
            <input
              type="checkbox"
              checked={mutationConfirmed}
              onChange={(e) => setMutationConfirmed(e.target.checked)}
            />
            I confirm this mutation test against staging backend
          </label>
        ) : null}
        {requiresUnsafeConfirm ? (
          <label className="cs-staging-check cs-staging-check--warn">
            <input
              type="checkbox"
              checked={unsafeConfirmed}
              onChange={(e) => setUnsafeConfirmed(e.target.checked)}
            />
            I understand POST may fail or create incomplete data (unsafe test)
          </label>
        ) : null}
        {requiresDestructiveConfirm ? (
          <label className="cs-staging-check cs-staging-check--danger">
            <input
              type="checkbox"
              checked={destructiveConfirmed}
              onChange={(e) => setDestructiveConfirmed(e.target.checked)}
            />
            I understand DELETE is destructive and irreversible
          </label>
        ) : null}
      </div>

      <button
        type="button"
        className={`cs-btn${requiresDestructiveConfirm ? " cs-btn--danger" : " cs-btn--primary"}`}
        disabled={!canRun}
        onClick={handleRun}
      >
        {loading ? "Running…" : `Run ${method} test`}
      </button>

      <StagingTestResult result={result} label={title} />
    </div>
  );
}

export function StagingInput({ label, name, inputs, setInputs, placeholder, type = "text" }) {
  return (
    <label className="cs-staging-input">
      <span>{label}</span>
      <input
        type={type}
        value={inputs[name] || ""}
        placeholder={placeholder}
        onChange={(e) =>
          setInputs((prev) => ({ ...prev, [name]: e.target.value }))
        }
      />
    </label>
  );
}

export function StagingFileInput({ label, name, inputs, setInputs, accept }) {
  return (
    <label className="cs-staging-input">
      <span>{label}</span>
      <input
        type="file"
        accept={accept}
        onChange={(e) =>
          setInputs((prev) => ({
            ...prev,
            [name]: e.target.files?.[0] || null,
          }))
        }
      />
    </label>
  );
}
